/**
 * Board 3 A · Approve (480) — `docs/job-stages.md` §8 A, from the job's
 * primary or the quote's: the same popup.
 *
 * In the board's order: **Who approved** (the property's contacts, starting on
 * the one the proposal is prepared for; a typed name when it has none) and
 * **When** (now; not in the future, not before it was sent) — both required —
 * then **How**, **What <first name> chose** (every choose-one group answered,
 * the optional extras ticked, starting on the proposal's starting selection),
 * **What <first name> said**, **Proof**, ☑ *Email <first name> the approved
 * quote to keep*, and the grey box `GET /quotes/:id/approve-preview` answers
 * for the selection. How, what was said and proof are optional until the
 * company requires them (Board 3 C); the server checks the same (`422`).
 *
 * `POST /quotes/:id/approve` with Board 3 A's fields; `onDone` gets its answer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  visitIncluded,
  type ApprovePreviewAnswer, type OfficeApprovalAnswer, type OfficeApprovalHow, type ProposalSelection,
  type ProposalView,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { formatDateOnly, formatMoneyText, formatRate } from '../lib/format.js'
import { usePhone } from '../lib/use-narrow.js'
import { MenuButton, MenuSelect, type MenuOption } from '../components/menu-select.js'
import { useRecordUpload } from '../record/use-record-upload.js'
import { timeIn } from '../visit/format.js'
import { StageGlyph } from './action-glyphs.js'
import { approveQuote, readApprovePreview, readProposal } from './api.js'
import { APPROVAL_HOW, APPROVE, COMMON, quoteVersion } from './copy.js'
import {
  ChipRow, FieldLabel, Fields, PillChip, RadioDot, Refusal, StagePopup, TextBox, TickBox, messageOf,
} from './popup.js'
import {
  approveProblems, chooseOf, fromLocalInput, localInputValue, NOTHING_REQUIRED, startingContact, startingWhen,
  type ApproveForm,
} from './rules.js'

export interface ApproveQuoteProps {
  open: boolean
  /** The quote being approved — sent or viewed, on a Quoted job. */
  quoteId: string
  onClose: () => void
  /**
   * After the server approved it: the approval, the job, the total, who the
   * copy went to — and who approved, for the toast *<ref> v<n> approved by <name>*.
   */
  onDone: (answer: ApproveQuoteAnswer) => void
  /** The clock — a test's; the browser's otherwise. */
  now?: () => Date
  /** Forces the phone's sheet or the desktop box; left out, the window decides. */
  phone?: boolean
}

/** What `onDone` is told: the server's answer and who approved (the contact's name, or the typed one). */
export interface ApproveQuoteAnswer extends OfficeApprovalAnswer {
  approvedByName?: string
}

export function ApproveQuote(props: ApproveQuoteProps) {
  if (!props.open) return null
  return <ApproveQuoteBox {...props} />
}

/** The browser's zone — the zone `<input type="datetime-local">` speaks. */
const ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

function ApproveQuoteBox({ quoteId, onClose, onDone, now = () => new Date(), phone }: ApproveQuoteProps) {
  const [view, setView] = useState<ProposalView | null>(null)
  const [preview, setPreview] = useState<ApprovePreviewAnswer | null>(null)
  const [selection, setSelection] = useState<ProposalSelection | null>(null)
  const [form, setForm] = useState<ApproveForm | null>(null)
  const [emailCopy, setEmailCopy] = useState(true)
  const [proofName, setProofName] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const asked = useRef(0)

  /* The proposal (its groups and extras) and the facts, once; the box again for every selection. */
  useEffect(() => {
    let live = true
    Promise.all([readProposal(quoteId), readApprovePreview(quoteId)])
      .then(([proposal, facts]) => {
        if (!live) return
        const start = facts.initial
        const choose = chooseOf(proposal, start.choose)
        setView(proposal)
        setPreview(facts)
        setSelection({ ...start, choose })
        setForm({
          contactId: startingContact(facts.contacts, facts.preparedForContactId),
          typedName: '',
          hasContacts: facts.contacts.length > 0,
          when: startingWhen(now(), facts.sentAt),
          how: null,
          note: '',
          proofFileId: null,
          choose,
        })
      })
      .catch((cause: unknown) => { if (live) setError(messageOf(cause)) })
    return () => { live = false }
    /* `now` is a clock, not a dependency: reading it again must not reload the quote. */
  }, [quoteId])

  const choose = useCallback((next: ProposalSelection) => {
    setSelection(next)
    setForm((was) => (was ? { ...was, choose: next.choose } : was))
    const ask = ++asked.current
    readApprovePreview(quoteId, next)
      .then((facts) => { if (ask === asked.current) setPreview(facts) })
      .catch((cause: unknown) => { if (ask === asked.current) setError(messageOf(cause)) })
  }, [quoteId])

  const requires = preview?.requires ?? NOTHING_REQUIRED
  const problems = form && view
    ? approveProblems(form, { requires, groups: view.groups, now: now(), sentAt: preview?.sentAt ?? null })
    : null
  const firstName = form?.hasContacts
    ? preview?.contacts.find((one) => one.id === form.contactId)?.firstName ?? preview?.firstName
    : form?.typedName.trim().split(/\s+/)[0] || preview?.firstName

  async function approve() {
    if (!form || !selection || !preview || problems?.length) return
    setBusy(true)
    setError(null)
    try {
      const contact = preview.contacts.find((one) => one.id === form.contactId) ?? null
      const approvedByName = form.hasContacts ? contact?.name ?? '' : form.typedName.trim()
      const answer = await approveQuote(quoteId, {
        chooseSelections: selection.choose,
        optionalAdds: selection.visits,
        serviceAdds: selection.services,
        lineAdds: selection.lines,
        approvedByContactId: form.hasContacts ? contact?.id ?? null : null,
        signatureName: form.hasContacts ? contact?.name ?? null : form.typedName.trim(),
        approvedAt: form.when!.toISOString(),
        how: form.how,
        note: form.note.trim() || null,
        proofFileId: form.proofFileId,
        emailCopy,
      })
      onDone({ ...answer, approvedByName })
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setBusy(false)
    }
  }

  const quote = preview ? quoteVersion(preview.quote.reference, preview.quote.version) : ''
  const title = preview ? APPROVE.title(quote) : ''
  const sub = preview?.quote.sentOn
    ? COMMON.sentViewed(formatDateOnly(preview.quote.sentOn), preview.quote.viewedOn ? formatDateOnly(preview.quote.viewedOn) : null)
    : undefined

  return (
    <StagePopup
      width={480}
      title={title}
      {...(sub ? { sub } : {})}
      tone="accent"
      glyph="check"
      onClose={onClose}
      asDrawn="js-03 A"
      {...(phone === undefined ? {} : { phone })}
      buttons={[
        { label: APPROVE.cancel, tone: 'plain', onClick: onClose },
        {
          label: APPROVE.approve,
          tone: 'primary',
          glyph: 'check',
          disabled: busy || !problems || problems.length > 0,
          onClick: () => void approve(),
        },
      ]}
    >
      {form && view && preview && selection
        ? (
          <Fields>
            <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
              <WhoApproved
                preview={preview}
                form={form}
                onPick={(contactId) => setForm({ ...form, contactId })}
                onType={(typedName) => setForm({ ...form, typedName })}
              />
              <When
                when={form.when}
                now={now()}
                sentAt={preview.sentAt}
                invalid={Boolean(problems?.some((one) => one.startsWith('when')))}
                onChange={(when) => setForm({ ...form, when })}
              />
            </div>

            <div className="min-w-0">
              <FieldLabel id="approve-how" label={APPROVE.how} required={requires.how} optional={!requires.how} />
              <ChipRow labelledBy="approve-how">
                {(Object.keys(APPROVAL_HOW) as OfficeApprovalHow[]).map((how) => (
                  <PillChip
                    key={how}
                    label={APPROVAL_HOW[how]}
                    on={form.how === how}
                    onClick={() => setForm({ ...form, how: form.how === how ? null : how })}
                  />
                ))}
              </ChipRow>
            </div>

            <WhatTheyChose
              view={view}
              selection={selection}
              label={APPROVE.chose(firstName)}
              onChange={choose}
            />

            <div className="min-w-0">
              <FieldLabel
                htmlFor="approve-said"
                label={APPROVE.said(firstName)}
                required={requires.note}
                optional={!requires.note}
              />
              <TextBox id="approve-said" value={form.note} onChange={(note) => setForm({ ...form, note })} />
            </div>

            <Proof
              required={requires.proof}
              fileName={proofName}
              onUploaded={(fileId, name) => { setForm((was) => (was ? { ...was, proofFileId: fileId } : was)); setProofName(name) }}
            />

            <button
              type="button"
              role="checkbox"
              aria-checked={emailCopy}
              onClick={() => setEmailCopy((was) => !was)}
              className="flex items-center gap-2.5 text-left tabular-nums"
            >
              <TickBox on={emailCopy} />
              <span className="text-[13px]">{APPROVE.email(firstName)}</span>
            </button>

            <WhatItDoes preview={preview} />

            <Refusal message={error} />
          </Fields>
        )
        : <Refusal message={error} />}
    </StagePopup>
  )
}

// ---------------------------------------------------------------------------
// Who approved · When
// ---------------------------------------------------------------------------

/** The 22px initials disc, the accent pair at 10/700 (*KW*). */
function Initials({ name }: { name: string }) {
  const letters = name.split(/\s+/).filter(Boolean).map((part) => part[0]!.toUpperCase())
  const initials = letters.length > 1 ? `${letters[0]}${letters[letters.length - 1]}` : (letters[0] ?? '')
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-accent-soft text-[10px] font-bold tracking-[.02em] text-accent-dark"
    >
      {initials}
    </span>
  )
}

/** The board's 36px box: 0 11, rounded 8, 13px. */
const BOX = 'flex h-9 w-full min-w-0 items-center rounded-button border bg-surface px-[11px] text-[13px] tabular-nums text-text'

function WhoApproved({ preview, form, onPick, onType }: {
  preview: ApprovePreviewAnswer
  form: ApproveForm
  onPick: (contactId: string) => void
  onType: (name: string) => void
}) {
  const phone = usePhone()
  const chosen = preview.contacts.find((one) => one.id === form.contactId) ?? null
  const options: MenuOption[] = preview.contacts.map((one) => ({
    value: one.id, label: one.name, glyph: <Initials name={one.name} />,
  }))

  return (
    <div className="min-w-0">
      <FieldLabel htmlFor="approve-who" label={APPROVE.who} required />
      {!form.hasContacts && (
        <input
          id="approve-who"
          value={form.typedName}
          onChange={(event) => onType(event.target.value)}
          className={cn(BOX, 'border-line outline-none focus:border-accent-line focus:ring-2 focus:ring-accent-ring')}
        />
      )}
      {form.hasContacts && phone && (
        /* On a phone every choice is a sheet (build-exactly-as-drawn law 6). */
        <MenuSelect
          id="approve-who"
          label={APPROVE.who}
          value={form.contactId}
          onChange={(value) => { if (value) onPick(value) }}
          options={options}
          size="field"
          sheet={{ title: APPROVE.who }}
          noSearch
        />
      )}
      {form.hasContacts && !phone && (
        <MenuButton
          options={options}
          onPick={onPick}
          minWidth={210}
          className={cn(BOX, 'gap-2 border-line hover:border-line-strong')}
        >
          {chosen && <Initials name={chosen.name} />}
          <span id="approve-who" className="min-w-0 flex-1 truncate text-left">{chosen?.name}</span>
          <span className="flex shrink-0 text-text-faint"><StageGlyph name="chevronDown" size={13} /></span>
        </MenuButton>
      )}
    </div>
  )
}

/**
 * *When*: the board's 36px box reading *Fri Oct 9 · 8:14 PM*, over the
 * browser's own date-and-time picker — bounded by when it was sent and now.
 */
function When({ when, now, sentAt, invalid, onChange }: {
  when: Date | null
  now: Date
  sentAt: string | null
  invalid: boolean
  onChange: (when: Date | null) => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const shown = when ? APPROVE.whenValue(timeIn(ZONE, when.toISOString(), 'date'), timeIn(ZONE, when.toISOString(), 'ampm')) : ''
  return (
    <div className="min-w-0">
      <FieldLabel htmlFor="approve-when" label={APPROVE.when} required />
      <div className={cn(BOX, 'relative focus-within:border-accent-line focus-within:ring-2 focus-within:ring-accent-ring',
        invalid ? 'border-danger' : 'border-line')}>
        <span className="flex-1 truncate text-left">{shown}</span>
        <input
          ref={input}
          id="approve-when"
          type="datetime-local"
          value={when ? localInputValue(when) : ''}
          max={localInputValue(now)}
          {...(sentAt ? { min: localInputValue(new Date(sentAt)) } : {})}
          onChange={(event) => onChange(fromLocalInput(event.target.value))}
          onClick={() => { try { input.current?.showPicker?.() } catch { /* a browser without it opens on focus */ } }}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// What <first name> chose
// ---------------------------------------------------------------------------

interface Extra { id: string; name: string; amount: number; level: 'visits' | 'services' | 'lines' }

/**
 * The optional extras, as the selection stands: optional visits; optional
 * services and lines of what is included.
 */
function extrasOf(view: ProposalView, selection: ProposalSelection): Extra[] {
  const extras: Extra[] = []
  for (const visit of view.visits) {
    if (visit.kind === 'optional') extras.push({ id: visit.id, name: visit.name, amount: visit.price.total, level: 'visits' })
    if (!visitIncluded(visit, selection)) continue
    for (const service of visit.services) {
      if (service.optional) extras.push({ id: service.id, name: service.name, amount: service.priceTotal, level: 'services' })
      if (service.optional && !selection.services.includes(service.id)) continue
      for (const line of service.lines) {
        if (line.optional) extras.push({ id: line.id, name: line.name, amount: line.amountTotal, level: 'lines' })
      }
    }
  }
  return extras
}

function WhatTheyChose({ view, selection, label, onChange }: {
  view: ProposalView
  selection: ProposalSelection
  label: string
  onChange: (selection: ProposalSelection) => void
}) {
  const extras = useMemo(() => extrasOf(view, selection), [view, selection])
  if (view.groups.length === 0 && extras.length === 0) return null

  const toggle = (extra: Extra) => {
    const list = selection[extra.level]
    onChange({
      ...selection,
      [extra.level]: list.includes(extra.id) ? list.filter((id) => id !== extra.id) : [...list, extra.id],
    })
  }

  return (
    <div className="min-w-0">
      <FieldLabel id="approve-chose" label={label} />
      <div aria-labelledby="approve-chose" className="flex flex-col gap-2 rounded-card border border-line px-3 py-2.5">
        {view.groups.map((group) => (
          <div key={group.id} role="radiogroup" aria-label={group.name} className="flex flex-col gap-2">
            <b className="text-[12px] font-semibold text-text-muted">{group.name}</b>
            {group.options.map((option) => {
              const on = selection.choose[group.id] === option.id
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => onChange({ ...selection, choose: { ...selection.choose, [group.id]: option.id } })}
                  className="flex items-center gap-2.5 text-left tabular-nums"
                >
                  <RadioDot on={on} />
                  <span className="min-w-0 flex-1 text-[13px]"><b>{option.name}</b></span>
                  <span className="shrink-0 text-[13px]">{formatRate(option.price.total)}</span>
                </button>
              )
            })}
          </div>
        ))}
        {extras.map((extra) => {
          const on = selection[extra.level].includes(extra.id)
          return (
            <button
              key={extra.id}
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => toggle(extra)}
              className="flex items-center gap-2.5 text-left tabular-nums"
            >
              <TickBox on={on} />
              <span className="text-[13px]"><b>{extra.name}</b> {APPROVE.extra(formatRate(extra.amount))}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Proof
// ---------------------------------------------------------------------------

/**
 * The app's file drop (`useRecordUpload`, the one upload hook) in Board 3's
 * 40px dashed row: the paperclip and *Attach a photo, screenshot or email*,
 * then the file's own name once it is up.
 */
function Proof({ required, fileName, onUploaded }: {
  required: boolean
  fileName: string | null
  onUploaded: (fileId: string, name: string) => void
}) {
  const upload = useRecordUpload({
    filesAt: null,
    orgUnitId: '',
    canEdit: true,
    describe: false,
    onUploaded: (files) => {
      const last = files[files.length - 1]
      if (last) onUploaded(last.id, last.name)
    },
  })
  return (
    <div className="min-w-0">
      <FieldLabel label={APPROVE.proof} required={required} optional={!required} />
      <input {...upload.inputProps} multiple={false} />
      <button
        type="button"
        {...upload.dropProps}
        onClick={() => upload.openPicker()}
        disabled={upload.busy}
        className={cn('flex h-[42px] w-full items-center gap-2.5 rounded-card border border-dashed px-3 text-left tabular-nums',
          'text-[12.5px] text-text-muted',
          upload.over ? 'border-accent-line bg-accent-soft' : 'border-line-strong',
          upload.busy && 'opacity-60')}
      >
        <StageGlyph name="attach" size={14} />
        <span className="min-w-0 flex-1 truncate">{fileName ?? APPROVE.attach}</span>
      </button>
      <Refusal message={upload.error} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// The grey box
// ---------------------------------------------------------------------------

/** *Approved total, before tax* and what approving makes and closes — read-only. */
function WhatItDoes({ preview }: { preview: ApprovePreviewAnswer }) {
  const makes = APPROVE.makes(
    preview.visitCount,
    preview.visits,
    preview.replaces.map((one) => quoteVersion(one.reference, one.version)),
  )
  return (
    <div className="rounded-card bg-surface-alt px-3 py-2.5 text-[12.5px] leading-[1.5] text-text-body">
      <div className="mb-1 flex text-[13px] text-text">
        <span className="flex-1">{APPROVE.total}</span>
        <b>{formatMoneyText(preview.total)}</b>
      </div>
      {makes.head}
      {makes.names.map((name, index) => (
        <span key={name}>
          {index > 0 && makes.join(index, makes.names.length)}
          <b>{name}</b>
        </span>
      ))}
      {makes.tail}
    </div>
  )
}
